<template>
  <div class="mtp">
    <!-- ── Toolbar ── -->
    <div class="mtp-toolbar">
      <div>
        <div class="mtp-toolbar__title">Mail-Templates</div>
        <div class="mtp-toolbar__sub">
          Kundenmails in 13 Sprachen — Änderungen gelten sofort für alle künftigen Mails.
        </div>
      </div>
      <v-spacer />
      <v-select
        v-model="selectedTemplateId"
        :items="templateItems"
        outlined dense hide-details
        class="mtp-select"
        menu-props="offsetY"
      />
      <v-select
        v-model="selectedStoreKey"
        :items="storeItems"
        outlined dense hide-details
        class="mtp-select mtp-select--store"
        menu-props="offsetY"
        prepend-inner-icon="mdi-storefront-outline"
      />
      <v-text-field
        v-model="testOrderRef"
        outlined dense hide-details clearable
        class="mtp-select mtp-select--order"
        placeholder="#91798 (optional)"
        prepend-inner-icon="mdi-package-variant-closed"
        title="Testmail mit echter Bestellung: Sprache, Store, Kunde und Bilder kommen dann aus der Order"
      />
      <v-btn
        outlined rounded color="#7b5cd6"
        class="mtp-btn"
        :loading="sendingTest"
        @click="sendTestMail"
      >
        <v-icon small left>mdi-email-check-outline</v-icon>
        Testmail an mich
      </v-btn>
      <v-btn
        rounded depressed dark
        color="#7b5cd6"
        class="mtp-btn"
        :disabled="!isDirty"
        :loading="saving"
        @click="saveLang"
      >
        <v-icon small left>mdi-content-save-outline</v-icon>
        Speichern
      </v-btn>
    </div>

    <!-- ── Sprach-Pills mit Vollständigkeits-Ampel ── -->
    <div class="mtp-langs">
      <button
        v-for="l in langItems"
        :key="l.value"
        class="mtp-lang"
        :class="{ 'mtp-lang--active': selectedLang === l.value }"
        @click="switchLang(l.value)"
      >
        <span
          class="mtp-lang__dot"
          :class="completeness[l.value] === 0 ? 'mtp-lang__dot--ok' : 'mtp-lang__dot--missing'"
        />
        {{ l.text }}
        <span v-if="completeness[l.value] > 0" class="mtp-lang__count">{{ completeness[l.value] }}</span>
      </button>
    </div>

    <div v-if="template" class="mtp-grid">
      <!-- ── Editor ── -->
      <v-card class="mtp-card">
        <div class="mtp-card__head">
          <span class="mtp-card__title">
            <v-icon small class="mtp-card__icon">mdi-translate</v-icon>
            Texte — {{ langLabel(selectedLang) }}
          </span>
          <span v-if="selectedLang !== 'de'" class="mtp-muted">Referenz darunter: Deutsch</span>
        </div>

        <div v-for="key in textKeys" :key="key" class="mtp-key">
          <div class="mtp-key__label">
            <code>{{ key }}</code>
            <span v-if="placeholderMismatch(key)" class="mtp-warn" title="Platzhalter weichen vom deutschen Original ab">
              <v-icon x-small color="#b26a00">mdi-code-braces</v-icon> Platzhalter prüfen
            </span>
          </div>
          <v-textarea
            v-model="draft[key]"
            :rows="rowsFor(key)"
            auto-grow dense outlined hide-details
            class="mtp-key__input"
            :class="{ 'mtp-key__input--empty': !String(draft[key] || '').trim() }"
          />
          <div v-if="selectedLang !== 'de'" class="mtp-key__reference">
            {{ deTexts[key] }}
          </div>
        </div>
      </v-card>

      <!-- ── Vorschau ── -->
      <v-card class="mtp-card mtp-card--preview">
        <div class="mtp-card__head">
          <span class="mtp-card__title">
            <v-icon small class="mtp-card__icon">mdi-eye-outline</v-icon>
            Vorschau
          </span>
          <span class="mtp-subject" :title="previewSubject">{{ previewSubject }}</span>
        </div>
        <iframe
          class="mtp-preview"
          :srcdoc="previewHtml"
          sandbox=""
          title="Mail-Vorschau"
        />
      </v-card>
    </div>

    <v-overlay :value="loading" opacity="0.15">
      <v-progress-circular indeterminate color="#7b5cd6" />
    </v-overlay>
  </div>
</template>

<script>
import { collection, doc, onSnapshot, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions as fbFunctions } from "../../../../fb";
import { EventBus, events } from "../../../../event-bus";

// Anzeige-Namen der Templates (IDs = transactionalMailTemplates-Doc-IDs)
const TEMPLATE_LABELS = {
  updates_artwork: "Live-Vorschau / Refinement",
  send_results_link: "Ergebnis — Customizer",
  send_results_digital: "Ergebnis — Digital",
  send_results_link_preview: "Gratis-Vorschau + Coupon",
  action_invalid_image: "Upload-Alternativen (Rahmen)",
};

const LANGS = [
  { value: "de", text: "DE" },
  { value: "en", text: "EN" },
  { value: "fr", text: "FR" },
  { value: "es", text: "ES" },
  { value: "it", text: "IT" },
  { value: "nl", text: "NL" },
  { value: "pl", text: "PL" },
  { value: "pt", text: "PT" },
  { value: "da", text: "DA" },
  { value: "sv", text: "SV" },
  { value: "cs", text: "CS" },
  { value: "sl", text: "SL" },
  { value: "sk", text: "SK" },
];

const LANG_NAMES = {
  de: "Deutsch", en: "Englisch", fr: "Französisch", es: "Spanisch", it: "Italienisch",
  nl: "Niederländisch", pl: "Polnisch", pt: "Portugiesisch", da: "Dänisch",
  sv: "Schwedisch", cs: "Tschechisch", sl: "Slowenisch", sk: "Slowakisch",
};

// Store-Sicht für Vorschau/Testversand — Branding-Spiegel von
// shopBasedEnvVariables in functions/utils.js (DOMAIN/LOGO_URL/EMAIL_REPLY_TO).
// Bei Änderungen dort BEIDE Stellen pflegen.
const STORES = [
  { key: "AQ_DE", shopId: "AQ", countryCode: "DE", text: "Artquarell.com", defaultLang: "de",
    brandName: "Artquarell.com", supportEmail: "du@artquarell.com",
    logoUrl: "https://artquarell.com/cdn/shop/files/logo_new_aq.png?v=1785858513&width=600" },
  { key: "AQ_ES", shopId: "AQ", countryCode: "ES", text: "Artcuarela.es", defaultLang: "es",
    brandName: "Artcuarela.es", supportEmail: "lisa@artcuarela.es",
    logoUrl: "https://artcuarela.es/cdn/shop/files/logo_right_360x.png?v=1669124511" },
  { key: "AQ_FR", shopId: "AQ", countryCode: "FR", text: "Artquarelle.fr", defaultLang: "fr",
    brandName: "Artquarelle.fr", supportEmail: "support@artquarelle.fr",
    logoUrl: "https://artquarelle.fr/cdn/shop/files/logo_FR-min.png?v=1724933265&width=500" },
  { key: "AQ_PL", shopId: "AQ", countryCode: "PL", text: "Artkwarela.pl", defaultLang: "pl",
    brandName: "Artkwarela.pl", supportEmail: "ty@artkwarela.pl", logoUrl: "" },
  { key: "TM_DE", shopId: "TM", countryCode: "DE", text: "TierBeiMir.com", defaultLang: "de",
    brandName: "TierBeiMir.com", supportEmail: "lisa@tierbeimir.com", logoUrl: "" },
  { key: "OH_US", shopId: "OH", countryCode: "US", text: "OhMy.art", defaultLang: "en",
    brandName: "OhMy.art", supportEmail: "support@ohmy.art",
    logoUrl: "https://cdn.shopify.com/s/files/1/0895/9112/0219/files/ohmyart_loogo_large_fd739e6a-be5b-4e4a-9181-069174ebf16e_430x.png" },
];

// Beispieldaten für die Vorschau — Spiegel von SAMPLE_DATA in
// functions/artquarellMain/TransactionalMail/transactionalMail.js
const SAMPLE_DATA = {
  customerName: "Malte",
  orderName: "#TEST",
  resultUrl: "https://artquarell.com/cdn/shop/files/logo_new_aq.png?v=1785858513&width=600",
  resultThumbnailUrl: "https://artquarell.com/cdn/shop/files/logo_new_aq.png?v=1785858513&width=600",
  mockupUrl: "https://artquarell.com/cdn/shop/files/logo_new_aq.png?v=1785858513&width=600",
  downloadsUrl: "https://status.artquarell.com",
  rateUpUrl: "https://status.artquarell.com",
  rateDownUrl: "https://status.artquarell.com",
  supportMessageUrl: "https://status.artquarell.com",
  uploadAlternativesUrl: "https://status.artquarell.com",
  noAlternativesUrl: "https://status.artquarell.com",
  couponCode: "TESTCODE5",
  subject: "Test-Betreff (Team-Text)",
  message: "<p>Test-Nachricht — hier steht im Ernstfall der im Dialog verfasste Team-Text.</p>",
  brandName: "Artquarell.com",
  supportEmail: "du@artquarell.com",
  logoUrl: "https://artquarell.com/cdn/shop/files/logo_new_aq.png?v=1785858513&width=600",
  noLogo: false,
};

// Mini-Renderer — 1:1-Spiegel von AbandonedCheckout/templateRenderer.js
const renderConditionals = (html, data) =>
  html.replace(/\{\{#if\s+([a-zA-Z0-9_.]+)\}\}([\s\S]*?)\{\{\/if\}\}/g, (_, key, body) => (data[key] ? body : ""));
const renderData = (html, data) =>
  html.replace(/\{\{([a-zA-Z0-9_]+)\}\}/g, (_, key) => {
    const v = data[key];
    return v === undefined || v === null ? "" : String(v);
  });
const renderTexts = (html, texts) =>
  html.replace(/\{\{t\.([a-zA-Z0-9_]+)\}\}/g, (_, key) => {
    const v = texts?.[key];
    return v === undefined || v === null ? "" : String(v);
  });

const extractPlaceholders = (s) => (String(s || "").match(/\{\{[a-zA-Z0-9_]+\}\}/g) || []).sort().join(",");

export default {
  name: "MailTemplatesPanel",
  data() {
    return {
      templates: {},
      selectedTemplateId: "updates_artwork",
      selectedStoreKey: "AQ_DE",
      selectedLang: "de",
      testOrderRef: "",
      draft: {},
      loading: true,
      saving: false,
      sendingTest: false,
      unsubscribe: null,
    };
  },
  computed: {
    templateItems() {
      return Object.keys(this.templates).map((id) => ({
        value: id,
        text: TEMPLATE_LABELS[id] || id,
      }));
    },
    langItems() {
      return LANGS;
    },
    storeItems() {
      return STORES.map((s) => ({ value: s.key, text: s.text }));
    },
    selectedStore() {
      return STORES.find((s) => s.key === this.selectedStoreKey) || STORES[0];
    },
    template() {
      return this.templates[this.selectedTemplateId] || null;
    },
    deTexts() {
      return this.template?.texts?.de || {};
    },
    textKeys() {
      // Reihenfolge: subject zuerst, dann wie im deutschen Original
      const keys = Object.keys(this.deTexts);
      return ["subject", ...keys.filter((k) => k !== "subject")].filter((k) => keys.includes(k));
    },
    savedLangTexts() {
      return this.template?.texts?.[this.selectedLang] || {};
    },
    isDirty() {
      return this.textKeys.some((k) => (this.draft[k] || "") !== (this.savedLangTexts[k] || ""));
    },
    // Fehlende/leere Keys je Sprache (gegen das deutsche Key-Set)
    completeness() {
      const out = {};
      const keys = Object.keys(this.deTexts);
      for (const { value: lang } of LANGS) {
        const t = this.template?.texts?.[lang] || {};
        out[lang] = keys.filter((k) => !String(t[k] || "").trim()).length;
      }
      return out;
    },
    previewRendered() {
      if (!this.template?.html) return { html: "", subject: "" };
      const texts = { ...this.savedLangTexts, ...this.draft };
      const store = this.selectedStore;
      const merged = {
        ...texts,
        ...SAMPLE_DATA,
        brandName: store.brandName,
        supportEmail: store.supportEmail,
        logoUrl: store.logoUrl,
        noLogo: !store.logoUrl,
        langCode: this.selectedLang,
      };
      let html = renderTexts(String(this.template.html), texts);
      html = renderConditionals(html, merged);
      html = renderData(html, merged);
      const subject = renderData(renderConditionals(String(texts.subject || ""), merged), merged);
      return { html, subject };
    },
    previewHtml() {
      return this.previewRendered.html;
    },
    previewSubject() {
      return this.previewRendered.subject;
    },
  },
  watch: {
    selectedTemplateId() {
      this.resetDraft();
    },
    // Store-Wechsel springt auf dessen Standardsprache — das ist die Sicht,
    // die die meisten Kunden dieses Stores bekommen
    selectedStoreKey() {
      this.switchLang(this.selectedStore.defaultLang);
    },
    savedLangTexts() {
      // Fremd-Update (anderer Tab/Seed) nur übernehmen, wenn lokal nichts editiert ist
      if (!this.isDirty) this.resetDraft();
    },
  },
  created() {
    this.unsubscribe = onSnapshot(collection(db, "transactionalMailTemplates"), (snap) => {
      const next = {};
      snap.forEach((d) => {
        next[d.id] = d.data();
      });
      this.templates = next;
      this.loading = false;
      if (!Object.keys(this.draft).length) this.resetDraft();
    });
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    langLabel(code) {
      return LANG_NAMES[code] || code;
    },
    switchLang(lang) {
      if (this.isDirty && !confirm("Ungespeicherte Änderungen verwerfen?")) return;
      this.selectedLang = lang;
      this.resetDraft();
    },
    rowsFor(key) {
      return String(this.deTexts[key] || "").length > 90 ? 3 : 1;
    },
    resetDraft() {
      const d = {};
      for (const k of this.textKeys) d[k] = this.savedLangTexts[k] || "";
      this.draft = d;
    },
    placeholderMismatch(key) {
      if (this.selectedLang === "de") return false;
      return extractPlaceholders(this.draft[key]) !== extractPlaceholders(this.deTexts[key]);
    },
    async saveLang() {
      this.saving = true;
      try {
        const clean = {};
        for (const k of this.textKeys) clean[k] = this.draft[k] || "";
        await updateDoc(doc(db, "transactionalMailTemplates", this.selectedTemplateId), {
          [`texts.${this.selectedLang}`]: clean,
          updatedAt: new Date().toISOString(),
        });
        EventBus.$emit(events.SNACKBAR, {
          message: `${this.langLabel(this.selectedLang)} gespeichert`,
          type: "success",
        });
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Speichern fehlgeschlagen: ${e.message}`, type: "error" });
      } finally {
        this.saving = false;
      }
    },
    async sendTestMail() {
      this.sendingTest = true;
      try {
        if (this.isDirty) {
          await this.saveLang();
        }
        const call = httpsCallable(fbFunctions, "sendTransactionalTestMail");
        const res = await call({
          templateId: this.selectedTemplateId,
          lang: this.selectedLang,
          shopId: this.selectedStore.shopId,
          countryCode: this.selectedStore.countryCode,
          // Mit Order-Referenz kommen Sprache/Store/Kunde/Bilder aus der
          // echten Bestellung — Sprach-/Store-Auswahl oben wird dann ignoriert
          orderRef: (this.testOrderRef || "").trim() || undefined,
        });
        if (res?.data?.status === "ok") {
          const src = res.data.order ? `Bestellung ${res.data.order}` : res.data.store;
          EventBus.$emit(events.SNACKBAR, { message: `Testmail ${src} (${res.data.lang}) an ${res.data.email} gesendet`, type: "success" });
        } else {
          EventBus.$emit(events.SNACKBAR, { message: `Testversand fehlgeschlagen: ${res?.data?.message}`, type: "error" });
        }
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Testversand fehlgeschlagen: ${e.message}`, type: "error" });
      } finally {
        this.sendingTest = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$accent: #7b5cd6;

.mtp {
  padding: 4px 2px 24px;
  color: #1d1d28;
}

/* ── Toolbar ── */
.mtp-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;

  &__title {
    font-size: 19px;
    font-weight: 700;
    letter-spacing: -0.2px;
  }
  &__sub {
    font-size: 12.5px;
    color: rgba(0, 0, 0, 0.5);
  }
}
.mtp-select {
  max-width: 280px;

  &--store {
    max-width: 210px;
  }
  &--order {
    max-width: 185px;
  }
  &--store,
  &--order {
    ::v-deep .v-input__icon--prepend-inner .v-icon {
      color: #7b5cd6;
      font-size: 18px;
    }
  }
  ::v-deep .v-input__slot {
    background: #fff !important;
    border-radius: 10px;
    box-shadow: 0 1px 4px rgba(20, 20, 40, 0.08) !important;
    fieldset { border-color: transparent; }
  }
}
.mtp-btn {
  text-transform: none;
  font-weight: 700;
  letter-spacing: 0.1px;
}

/* ── Sprach-Pills ── */
.mtp-langs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}
.mtp-lang {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: none;
  outline: none;
  cursor: pointer;
  background: #fff;
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 12.5px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.62);
  box-shadow: 0 1px 4px rgba(20, 20, 40, 0.08);
  transition: background 0.15s, color 0.15s, box-shadow 0.15s;

  &:hover {
    box-shadow: 0 2px 8px rgba(20, 20, 40, 0.13);
  }
  &--active {
    background: $accent;
    color: #fff;
    box-shadow: 0 4px 12px rgba(123, 92, 214, 0.35);
  }

  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;

    &--ok { background: #4caf50; }
    &--missing { background: #ef5350; }
  }
  &--active &__dot--ok { background: #b5f0b8; }
  &--active &__dot--missing { background: #ffc4c1; }

  &__count {
    background: #fdecea;
    color: #c62828;
    border-radius: 999px;
    font-size: 10.5px;
    padding: 1px 6px;
  }
  &--active &__count {
    background: rgba(255, 255, 255, 0.22);
    color: #fff;
  }
}

/* ── Layout: Editor + Vorschau ── */
.mtp-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
}
.mtp-card {
  padding: 18px 20px;

  &__head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
    min-height: 28px;
  }
  &__title {
    font-size: 14.5px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    white-space: nowrap;
  }
  &__icon {
    color: $accent !important;
  }
}
.mtp-muted {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-left: auto;
}

/* ── Key-Editor ── */
.mtp-key {
  margin-bottom: 14px;

  &__label {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;

    code {
      background: #edeafa;
      color: #6a4fc4;
      font-size: 11px;
      font-weight: 700;
      border-radius: 6px;
      padding: 2px 8px;
    }
  }
  &__input {
    ::v-deep .v-input__slot {
      border-radius: 10px;
      background: #fff !important;
      fieldset { border-color: rgba(0, 0, 0, 0.12); }
    }
    ::v-deep textarea {
      font-size: 13.5px;
      line-height: 1.5;
    }
    &--empty ::v-deep .v-input__slot {
      background: #fdecea !important;
      fieldset { border-color: #ef9a9a; }
    }
  }
  &__reference {
    font-size: 12px;
    line-height: 1.5;
    color: rgba(0, 0, 0, 0.42);
    margin-top: 4px;
    padding-left: 2px;
    white-space: pre-wrap;
  }
}
.mtp-warn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #fff3e0;
  color: #b26a00;
  font-size: 10.5px;
  font-weight: 700;
  border-radius: 999px;
  padding: 2px 9px;
}

/* ── Vorschau ── */
.mtp-card--preview {
  position: sticky;
  top: 16px;
  align-self: start;
}
.mtp-subject {
  margin-left: auto;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: #edeafa;
  color: #6a4fc4;
  font-size: 12px;
  font-weight: 700;
  border-radius: 999px;
  padding: 4px 12px;
}
.mtp-preview {
  width: 100%;
  height: 76vh;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background: #fff;
}
</style>
